import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { get } from '../lib/api';
import { AgentStatusView } from '../lib/types';
import { Input } from './ui';

interface DirectoryEntry {
  userId: string;
  name: string;
  extension: string;
  online: boolean;
}

/**
 * Campo de ramal com busca: sugere os usuários (nome e ramal) e a fila de ligações; também aceita
 * digitar qualquer ramal ou número. `onPick` dispara ao escolher uma sugestão (Enter também).
 */
export function ExtensionPicker({
  value,
  onChange,
  onPick,
  placeholder = 'Nome, ramal ou número',
  excludeExtension,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick?: (extension: string) => void;
  placeholder?: string;
  excludeExtension?: string | null;
  autoFocus?: boolean;
}) {
  const directory = useQuery({ queryKey: ['telephony', 'directory'], queryFn: () => get<DirectoryEntry[]>('/telephony/directory'), staleTime: 60_000 }).data ?? [];
  const queue = useQuery({ queryKey: ['telephony', 'me'], queryFn: () => get<AgentStatusView>('/telephony/me/status') }).data?.queue ?? null;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const options = useMemo(() => {
    const q = value.trim().toLowerCase();
    const all = [
      ...(queue ? [{ key: `fila-${queue}`, extension: queue, label: `Fila de ligações ${queue}`, hint: 'qualquer operador disponível', online: true }] : []),
      ...directory
        .filter((d) => d.extension !== excludeExtension)
        .map((d) => ({ key: d.userId, extension: d.extension, label: d.name, hint: `ramal ${d.extension}`, online: d.online })),
    ];
    return (q ? all.filter((o) => o.label.toLowerCase().includes(q) || o.extension.includes(q)) : all).slice(0, 8);
  }, [value, directory, queue, excludeExtension]);

  const pick = (extension: string) => {
    onChange(extension);
    setOpen(false);
    onPick?.(extension);
  };

  return (
    <div className="relative flex-1">
      <Input
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        inputMode="text"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || options.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && value.trim() && !/^[\d*#+]+$/.test(value.trim())) {
            // Texto (nome): Enter escolhe a sugestão destacada. Número digitado: segue como está.
            e.preventDefault();
            pick(options[active].extension);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && options.length > 0 && (
        <div className="absolute top-full right-0 left-0 z-40 mt-1 max-h-60 overflow-y-auto rounded-md border border-border bg-panel py-1 shadow-lg">
          {options.map((o, i) => (
            <button
              key={o.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o.extension)}
              className={clsx('flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm', i === active ? 'bg-muted' : 'hover:bg-muted')}
            >
              <span className={clsx('size-2 shrink-0 rounded-full', o.online ? 'bg-ok' : 'bg-border')} />
              <span className="min-w-0 flex-1 truncate">{o.label}</span>
              <span className="font-mono text-xs text-fg-soft">{o.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
